import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Gate } from './Gate';
import type { Model } from './viewer/lib/model';
import type { TransactionRow } from './viewer/lib/types';
import type { SyncUi } from './viewer/components/Shell';
import { CategoryIcon } from './viewer/components/CategoryIcon';
import { useSyncStore } from '@/stores/useSyncStore';
import { afterLocalWrite, backupWhen, boot, connectGoogle, connectedEmail, disconnectGoogle, joinSpace, leave, loadModel, openDriveBackup, pendingUploads, reconnectGoogle, rememberDriveBackup, restoreDemo, restoreText, savedDriveBackup, syncEnabled, syncOnce, type DriveBackupRef } from './session';
import { RECONNECT_MESSAGE, hasGoogleToken, isReconnectError } from './shims/google-signin';
import { plural } from './viewer/lib/text';
import { TransactionForm, TX_KIND_ICON, TX_KIND_LABEL, type TxKind } from './forms/TransactionForm';
import { AccountForm } from './forms/AccountForm';
import { CategoryDialog } from './forms/CategoryDialogs';
import { CardForm } from './forms/CardForm';
import { Modal } from './forms/ui';
import { createTxEditor } from './forms/txEditor';
import { createAccountsEditor } from './forms/accountsEditor';
import { createCardsEditor } from './forms/cardsEditor';
import { createFinancingsEditor } from './forms/financingsEditor';
import { createLoansEditor } from './forms/loansEditor';
import { createRecurringEditor } from './forms/recurringEditor';
import { createBudgetsEditor } from './forms/budgetsEditor';
import { createSavingsEditor } from './forms/savingsEditor';
import { createCategoriesEditor } from './forms/categoriesEditor';
import { ConfirmHost, askConfirm } from './forms/confirm';

// As telas (Recharts + ícones) só baixam depois que há dados: a porta fica leve.
const Dashboard = lazy(() => import('./viewer/components/Dashboard').then((m) => ({ default: m.Dashboard })));

/** `driveBackup`: a base veio do backup do Drive (sem sync); `when` é de quando ele é, já formatado. */
type Source = { kind: 'demo' } | { kind: 'file'; name: string; driveBackup?: { when: string | null } };
const driveBackupSource = (stamp: string | null): Source => {
  const when = backupWhen(stamp, true);
  return { kind: 'file', name: when ? `Backup de ${when}` : 'Backup do Drive', driveBackup: { when } };
};
type Sheet = { kind: 'menu' } | { kind: 'tx'; tx?: TransactionRow; initialKind?: TxKind; initialAccountId?: string; initialDate?: string; initialCategoryId?: string; initialSubcategoryId?: string } | { kind: 'account' } | { kind: 'category' } | { kind: 'card' };
const SYNC_POLL_MS = 120_000;
const noop = () => undefined;
const SYNC_MIN_GAP_MS = 20_000;

const TX_HINT: Record<TxKind, string> = {
  expense: 'Saiu de uma conta',
  income: 'Entrou numa conta',
  card: 'Vai pra fatura do cartão',
  transfer: 'De uma conta pra outra',
};

export default function App() {
  const [phase, setPhase] = useState<'boot' | 'gate' | 'open'>('boot');
  const [model, setModel] = useState<Model | null>(null);
  const [source, setSource] = useState<Source>({ kind: 'file', name: 'Kontto Web' });
  const [sync, setSync] = useState<SyncUi | null>(null);
  /** escritas daqui que ainda não subiram pro Drive (a faixa e o botão mostram) */
  const [pending, setPending] = useState(0);
  const [sheet, setSheet] = useState<Sheet | null>(null);
  const lastCheck = useRef(0);

  const refresh = useCallback(async () => { setModel(await loadModel()); }, []);
  const refreshPending = useCallback(async () => { setPending(await pendingUploads()); }, []);

  // Erro que só um clique resolve (autorização do Google vencida, popup bloqueado) vira "reconnect" — também
  // quando o motor de sync traduziu a falha em outra mensagem: sem token válido na aba, a causa é essa.
  const needsReconnect = (error: string | null | undefined) => !!error && (isReconnectError(error) || !hasGoogleToken());
  const syncUiFromStore = useCallback((message: string | null, status?: SyncUi['status']): SyncUi | null => {
    if (!syncEnabled()) return null;
    const s = useSyncStore.getState();
    const reconnect = status === 'reconnect' || (status == null && needsReconnect(s.lastError));
    const msg = reconnect ? (message && isReconnectError(message) ? message : RECONNECT_MESSAGE) : (message ?? s.lastError ?? null);
    return {
      status: status ?? (reconnect ? 'reconnect' : s.lastError ? 'error' : 'idle'),
      lastAt: s.lastSyncAt ? new Date(s.lastSyncAt) : null,
      latest: s.lastSyncAt ? new Date(s.lastSyncAt) : null,
      message: msg,
      segments: 0,
      skipped: 0,
      pending: 0,
    };
  }, []);

  const checkSync = useCallback(async (manual: boolean) => {
    if (!syncEnabled()) return;
    if (!manual && Date.now() - lastCheck.current < SYNC_MIN_GAP_MS) return;
    setSync((u) => (u ? { ...u, status: 'checking', message: null } : u));
    const r = await syncOnce();
    lastCheck.current = Date.now();
    if (r.ok) await refresh();
    await refreshPending();
    setSync(syncUiFromStore(r.ok ? (manual ? 'Em dia' : null) : (r.error ?? 'Não deu pra sincronizar.'), r.ok ? 'idle' : needsReconnect(r.error) ? 'reconnect' : 'error'));
  }, [refresh, refreshPending, syncUiFromStore]);

  /** Botão Sincronizar/Reconectar e a faixa: o token do Google é pedido ANTES de qualquer await do ciclo, ainda no clique. */
  const manualSync = useCallback(async () => {
    if (!syncEnabled()) return;
    try { await reconnectGoogle(); } catch (e) { setSync(syncUiFromStore(e instanceof Error ? e.message : String(e), 'reconnect')); return; }
    await checkSync(true);
  }, [checkSync, syncUiFromStore]);

  // O envio pós-escrita roda fora do checkSync: acompanha o store pra faixa e o botão não ficarem
  // dizendo "sincronizado" enquanto a autorização já venceu (e pra contagem de pendentes baixar
  // quando o envio dá certo).
  useEffect(() => useSyncStore.subscribe(() => {
    if (!syncEnabled()) return;
    setSync((u) => (u?.status === 'checking' ? u : syncUiFromStore(null)));
    void refreshPending();
  }), [syncUiFromStore, refreshPending]);

  // Boot: cópia deste navegador (IndexedDB) ou banco vazio. Com dados, abre direto nas telas.
  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const { hasData } = await boot();
        if (!alive) return;
        if (hasData) {
          await refresh();
          const email = connectedEmail();
          const fromBackup = email ? null : savedDriveBackup();
          setSource(fromBackup ? driveBackupSource(fromBackup.stamp) : { kind: 'file', name: email ? `Sync · ${email}` : 'Este navegador' });
          setSync(syncUiFromStore(null));
          void refreshPending();
          setPhase('open');
          if (syncEnabled()) void checkSync(true);
        } else {
          setPhase('gate');
        }
      } catch (e) {
        console.error('[kontto-web] boot falhou:', e);
        if (alive) setPhase('gate');
      }
    })();
    return () => { alive = false; };
  }, [refresh, refreshPending, checkSync, syncUiFromStore]);

  // WebMCP: ferramentas de consulta pro agente de IA enquanto os dados estão abertos; na porta, só a
  // que ensina a entrar. A dependência é `model` (o sync troca o modelo por baixo, e ferramenta presa
  // ao antigo responderia número velho). Import dinâmico pra porta não pagar o peso; falhar aqui não
  // pode tirar a página do ar — sem WebMCP tudo funciona igual.
  useEffect(() => {
    if (phase === 'boot') return;
    let cancel: (() => void) | null = null;
    let alive = true;
    const load = import('./viewer/lib/agentTools').then(m => (model ? m.registerAppTools(model) : m.registerGateTools()));
    void load.then(c => { if (alive) cancel = c; else c(); }).catch(() => {});
    return () => { alive = false; cancel?.(); };
  }, [phase, model]);

  // Verificação automática: a cada 2 min com a aba visível, e ao voltar pra ela.
  useEffect(() => {
    if (phase !== 'open' || !sync) return;
    const tick = () => { if (document.visibilityState === 'visible') void checkSync(false); };
    const timer = window.setInterval(tick, SYNC_POLL_MS);
    document.addEventListener('visibilitychange', tick);
    window.addEventListener('focus', tick);
    return () => { window.clearInterval(timer); document.removeEventListener('visibilitychange', tick); window.removeEventListener('focus', tick); };
  }, [phase, sync, checkSync]);

  const joinedEmail = useRef('');
  async function onConnect(onStep: Parameters<typeof connectGoogle>[0]) {
    const entry = await connectGoogle(onStep);
    joinedEmail.current = entry.email;
    return entry;
  }

  async function onJoin(passphrase: string, onStep: Parameters<typeof joinSpace>[1]) {
    await joinSpace(passphrase, onStep);
    await refresh();
    rememberDriveBackup(false);
    setSource({ kind: 'file', name: `Sync · ${joinedEmail.current || connectedEmail() || ''}` });
    setSync(syncUiFromStore('Sincronizado'));
    setPhase('open');
  }

  /** Conta sem sincronização: abre o backup mais recente do Drive, como um arquivo (nada volta pro celular). */
  async function onOpenDriveBackup(backup: DriveBackupRef, passphrase: string) {
    await openDriveBackup(backup, passphrase);
    await refresh();
    rememberDriveBackup(backup.stamp);
    setSource(driveBackupSource(backup.stamp));
    setSync(null);
    setPhase('open');
  }

  async function onOpenFile(text: string, passphrase: string | null) {
    await restoreText(text, passphrase);
    await refresh();
    rememberDriveBackup(false);
    setSource({ kind: 'file', name: 'Arquivo aberto neste navegador' });
    setSync(null);
    setPhase('open');
  }

  async function onOpenDemo() {
    const { buildDemoPayload } = await import('./viewer/lib/demo');
    await restoreDemo(buildDemoPayload());
    await refresh();
    rememberDriveBackup(false);
    setSource({ kind: 'demo' });
    setSync(null);
    setPhase('open');
  }

  /** Depois de qualquer escrita: modelo novo nas telas e o sync agendado (se ligado). */
  const afterWrite = useCallback(async () => {
    await refresh();
    await afterLocalWrite();
    void refreshPending();
  }, [refresh, refreshPending]);
  const onSaved = useCallback(async () => {
    await afterWrite();
    setSheet(null);
  }, [afterWrite]);
  // Edição na célula da planilha de Transações: escreve pelos repositórios e recarrega o modelo.
  const editor = useMemo(() => createTxEditor(afterWrite), [afterWrite]);
  const accountsEditor = useMemo(() => createAccountsEditor(afterWrite), [afterWrite]);
  const cardsEditor = useMemo(() => createCardsEditor(afterWrite), [afterWrite]);
  const financingsEditor = useMemo(() => createFinancingsEditor(afterWrite), [afterWrite]);
  const loansEditor = useMemo(() => createLoansEditor(afterWrite), [afterWrite]);
  const recurringEditor = useMemo(() => createRecurringEditor(afterWrite), [afterWrite]);
  const budgetsEditor = useMemo(() => createBudgetsEditor(afterWrite), [afterWrite]);
  const savingsEditor = useMemo(() => createSavingsEditor(afterWrite), [afterWrite]);
  const categoriesEditor = useMemo(() => createCategoriesEditor(afterWrite), [afterWrite]);

  // Base aberta sem sincronização (arquivo, backup do Drive ou a cópia que ficou neste navegador): a faixa
  // diz que nada daqui vai pro celular. Sem ela a pessoa edita um monte e só descobre no telefone.
  const localOnly = sync || source.kind !== 'file' ? undefined : {
    title: source.driveBackup
      ? `Você está no backup do Drive${source.driveBackup.when ? ` de ${source.driveBackup.when}` : ''}. Esta cópia não sincroniza com o celular.`
      : 'Esta cópia não sincroniza com o celular.',
    text: 'O que você lançar ou editar aqui fica só neste navegador. Pra usar os dois juntos, ligue “Sincronizar aparelhos” no celular (Configurações › Backup), saia daqui e entre com o Google.',
  };

  if (phase === 'boot') return <div className="flex min-h-screen items-center justify-center text-[14px] text-mute">Abrindo…</div>;
  if (phase === 'gate' || !model) return <Gate onConnect={onConnect} onJoin={onJoin} onOpenDriveBackup={onOpenDriveBackup} onAbortJoin={disconnectGoogle} onOpenFile={onOpenFile} onOpenDemo={onOpenDemo} />;
  return (
    <Suspense fallback={<div className="flex min-h-screen items-center justify-center text-[14px] text-mute">Abrindo…</div>}>
      <Dashboard
        model={model}
        source={source}
        onClose={() => void (async () => {
          // Sair apaga a cópia daqui: o que ainda não subiu pro Drive se perde — avisar com o número.
          const n = await pendingUploads();
          const text = n > 0
            ? `${plural(n, 'alteração feita aqui ainda não subiu', 'alterações feitas aqui ainda não subiram')} pro Drive e ${n === 1 ? 'vai' : 'vão'} se perder. Reconecte e sincronize antes de sair. Apaga a cópia dos seus dados que está aqui; o celular continua igual.`
            : 'Apaga a cópia dos seus dados que está aqui. O celular continua igual, e dá pra entrar de novo quando quiser.';
          if (await askConfirm({ title: 'Sair deste navegador?', text, confirmLabel: n > 0 ? 'Sair mesmo assim' : 'Sair', danger: true })) await leave();
        })()}
        sync={sync ? { ...sync, pending, onSync: () => void manualSync() } : undefined}
        localOnly={localOnly}
        actions={{ onNew: () => setSheet({ kind: 'menu' }), onSelectTransaction: (tx) => setSheet({ kind: 'tx', tx }), editor, accounts: accountsEditor, cards: cardsEditor, financings: financingsEditor, loans: loansEditor, recurring: recurringEditor, budgets: budgetsEditor, savings: savingsEditor, categories: categoriesEditor, onNewTx: (o) => setSheet({ kind: 'tx', initialKind: o.cardId ? 'card' : o.type === 'income' ? 'income' : 'expense', initialAccountId: o.cardId ?? o.accountId, initialDate: o.date, initialCategoryId: o.categoryId, initialSubcategoryId: o.subcategoryId }) }}
      />
      {sheet?.kind === 'menu' && (
        <Modal title="Lançar" onClose={() => setSheet(null)} testId="new-menu">
          <div className="mt-4 grid grid-cols-2 gap-2">
            {(['expense', 'income', 'card', 'transfer'] as TxKind[]).map((k) => (
              <button key={k} type="button" onClick={() => setSheet({ kind: 'tx', initialKind: k })}
                className="flex items-center gap-3 rounded-xl border border-rule p-3.5 text-left hover:border-teal" data-testid={`new-tx-${k}`}>
                <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${k === 'income' ? 'bg-pos-pale text-pos' : k === 'expense' ? 'bg-neg-pale text-neg' : 'bg-teal-pale text-teal'}`}>
                  <CategoryIcon name={TX_KIND_ICON[k]} size={17} />
                </span>
                <span className="min-w-0">
                  <span className="block text-[14px] font-semibold text-ink">{TX_KIND_LABEL[k]}</span>
                  <span className="block text-[12px] text-mute">{TX_HINT[k]}</span>
                </span>
              </button>
            ))}
          </div>
          <p className="mt-5 text-[11px] font-semibold uppercase tracking-[0.1em] text-mute">Cadastros</p>
          <div className="mt-2 grid grid-cols-3 gap-2">
            {([
              ['account', 'Conta', 'bank'],
              ['card', 'Cartão', 'credit-card'],
              ['category', 'Categoria', 'tag'],
            ] as const).map(([k, title, icon]) => (
              <button key={k} type="button" onClick={() => setSheet({ kind: k })} className="flex items-center gap-2 rounded-xl border border-rule px-3 py-2.5 text-left text-[13px] font-semibold text-ink hover:border-teal" data-testid={`new-${k}`}>
                <CategoryIcon name={icon} size={14} className="text-mute" />{title}
              </button>
            ))}
          </div>
        </Modal>
      )}
      {sheet?.kind === 'tx' && <TransactionForm model={model} tx={sheet.tx} initialKind={sheet.initialKind} initialAccountId={sheet.initialAccountId} initialDate={sheet.initialDate} initialCategoryId={sheet.initialCategoryId} initialSubcategoryId={sheet.initialSubcategoryId} onClose={() => setSheet(null)} onSaved={onSaved} />}
      {sheet?.kind === 'account' && <AccountForm onClose={() => setSheet(null)} onSaved={onSaved} />}
      {sheet?.kind === 'category' && <CategoryDialog model={model} editor={categoriesEditor} onClose={() => setSheet(null)} onDone={noop} />}
      {sheet?.kind === 'card' && <CardForm model={model} onClose={() => setSheet(null)} onSaved={onSaved} />}
      <ConfirmHost />
    </Suspense>
  );
}
